/**
 * Swiss public holiday computation engine.
 *
 * SSOT for all Feiertage — canton-specific, algorithmically computed.
 * Uses the Anonymous Gregorian (Meeus-Jones-Butcher) algorithm for Easter.
 */

import type { Canton } from "@/types/insurance";

// ─── Types ──────────────────────────────────────────────────────────

export interface SwissHoliday {
  date: Date;
  name: string;
}

export interface CompanyClosure {
  /** ISO date string, inclusive */
  start: string;
  /** ISO date string, inclusive */
  end: string;
  description: string;
}

// ─── Easter computation (Anonymous Gregorian algorithm) ─────────────

/**
 * Compute Easter Sunday for a given year.
 * Algorithm: Meeus-Jones-Butcher (Anonymous Gregorian).
 */
export function computeEasterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day); // JS months are 0-based
}

// ─── Helper ─────────────────────────────────────────────────────────

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ─── Canton holiday definitions ─────────────────────────────────────

interface HolidayDef {
  /** Fixed: [month (1-based), day]. Easter-based: offset from Easter Sunday. */
  fixed?: [number, number];
  easterOffset?: number;
  name: string;
}

const ZH_HOLIDAYS: HolidayDef[] = [
  { fixed: [1, 1], name: "Neujahrstag" },
  { fixed: [1, 2], name: "Berchtoldstag" },
  { easterOffset: -2, name: "Karfreitag" },
  { easterOffset: 1, name: "Ostermontag" },
  { fixed: [5, 1], name: "Tag der Arbeit" },
  { easterOffset: 39, name: "Auffahrt" },
  { easterOffset: 50, name: "Pfingstmontag" },
  { fixed: [8, 1], name: "Bundesfeier" },
  { fixed: [12, 25], name: "Weihnachten" },
  { fixed: [12, 26], name: "Stephanstag" },
];

const GL_HOLIDAYS: HolidayDef[] = [
  { fixed: [1, 1], name: "Neujahrstag" },
  { fixed: [1, 2], name: "Berchtoldstag" },
  { easterOffset: -2, name: "Karfreitag" },
  { easterOffset: 1, name: "Ostermontag" },
  { fixed: [5, 1], name: "Tag der Arbeit" },
  { easterOffset: 39, name: "Auffahrt" },
  { easterOffset: 50, name: "Pfingstmontag" },
  { fixed: [8, 1], name: "Bundesfeier" },
  // Näfelser Fahrt (first Thursday in April)
  { fixed: [12, 25], name: "Weihnachten" },
  { fixed: [12, 26], name: "Stephanstag" },
];

const CANTON_HOLIDAYS: Record<string, HolidayDef[]> = {
  ZH: ZH_HOLIDAYS,
  GL: GL_HOLIDAYS,
};

// ─── Public holiday functions ───────────────────────────────────────

function resolveHolidays(year: number, defs: HolidayDef[]): SwissHoliday[] {
  const easter = computeEasterSunday(year);
  return defs.map((def) => {
    const date = def.fixed
      ? new Date(year, def.fixed[0] - 1, def.fixed[1])
      : addDays(easter, def.easterOffset!);
    return { date, name: def.name };
  });
}

/** Get all public holidays for a given year and canton. */
export function getPublicHolidays(year: number, canton: Canton | string): SwissHoliday[] {
  const defs = CANTON_HOLIDAYS[canton] ?? CANTON_HOLIDAYS.ZH;
  return resolveHolidays(year, defs);
}

/** Get public holidays that fall in a specific month (1-based). */
export function getPublicHolidaysInMonth(
  year: number,
  month: number,
  canton: Canton | string
): SwissHoliday[] {
  return getPublicHolidays(year, canton).filter(
    (h) => h.date.getFullYear() === year && h.date.getMonth() + 1 === month
  );
}

/** Check whether a date is a public holiday. */
export function isPublicHoliday(date: Date, canton: Canton | string): boolean {
  const key = dateKey(date);
  return getPublicHolidays(date.getFullYear(), canton).some(
    (h) => dateKey(h.date) === key
  );
}

// ─── Company closures (Betriebsferien) ─────────────────────────────

/**
 * Betriebsferien are separate from public holidays.
 * Configure per year here. This is the SSOT.
 */
const COMPANY_CLOSURES: CompanyClosure[] = [
  {
    start: "2025-12-22",
    end: "2026-01-04",
    description: "Betriebsferien Winter 2025/26",
  },
];

/** Check whether a date falls within a company closure period. */
export function isCompanyClosure(date: Date): boolean {
  const key = dateKey(date);
  return COMPANY_CLOSURES.some((c) => key >= c.start && key <= c.end);
}

/** Count company closure days in a given month that are business days and NOT already a public holiday. */
export function getCompanyClosureDaysInMonth(
  year: number,
  month: number,
  canton: Canton | string
): number {
  const daysInMonth = new Date(year, month, 0).getDate();
  let count = 0;
  for (let day = 1; day <= daysInMonth; day++) {
    const d = new Date(year, month - 1, day);
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue; // skip weekends
    if (isPublicHoliday(d, canton)) continue; // already counted as holiday
    if (isCompanyClosure(d)) count++;
  }
  return count;
}
